import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import { TextField, InputAdornment, IconButton } from "@material-ui/core";
import { MdClear, MdSearch } from "react-icons/md";

import TrafficOverview from "../Changes/TrafficOverview";
import DateRangeSelector, { PresetRow, PresetBtn } from "../TrendsDashboard/DateRangeSelector";
import FixtureGate from "../Changes/FixtureGate";
import Breakdown from "./Breakdown";
import Controls from "./Controls";
import CauseBreakdown from "./CauseBreakdown";
import PathBreakdown from "./PathBreakdown";
import { folderTree, withLoss, isFolder, isUnder } from "./folders";
import IssueRows from "./IssueRows";
import KpiBar from "./KpiBar";
import CriticalityFilters from "./CriticalityFilters";
import { ALL, countsOf, rowsUnder, criticalityFilterOf } from "./criticalityFilters";
import { CRITICALITIES, criticalityOf, highestOf, siteMonthlyOf } from "./criticality";
import { KPIS } from "./kpis";
import { CAUSES, causesOf } from "./kinds";
import { useIssuesFixture } from "./fixture";
import { presetsEndingOn, rangeOf, isInside, filterLabelOf, DEFAULT_RANGE, lastDayOf, firstDayOf } from "./range";
import { measureOf, measuresOf, measureKeyOf } from "./measure";
import { rowsOf, byImpact, byPageWeight, lossOf } from "./rows";
import { usePushParams } from "../CurrentMonitoredUri/commons";
import { ACCENT } from "./accent";

const MAX_ROWS = 50;
/** The order the loss column, the pill's tooltip and the Breakdown card write the measures in, impressions first. */
const LOSS_ORDER = ["impressions", "sessions"];
// No `?zoom=`, `?from=` or `?to=` is the default 12 months.
const DEFAULT_RANGE_PARAMS = { zoom: null, from: null, to: null };

const daysOf = ({ start, end }) => {
    const days = [];
    for (let day = start; day <= end; day = new Date(Date.parse(`${day}T00:00:00Z`) + 86400000).toISOString().slice(0, 10)) days.push(day);
    return days;
};

/** How many of `rows` the list shows: all of them, or the first `MAX_ROWS` of how many. */
const shownOf = (rows) => (rows.length > MAX_ROWS ? `the first ${MAX_ROWS} of ${rows.length.toLocaleString()}` : rows.length.toLocaleString());

const SORTS = {
    impact: { label: "impact", compare: byImpact },
    importance: { label: "importance", compare: byPageWeight },
};

export default function Issues() {
    const { fixture, error } = useIssuesFixture();
    if (!fixture) {
        return <FixtureGate error={error} />;
    }
    return <Page fixture={fixture} />;
}

/** The page on a loaded fixture. */
function Page({ fixture }) {
    const location = useLocation();
    // The figures the KPI bar has put on the chart; none chosen yet means the row charts' measure.
    const [chartedKeys, setChartedKeys] = useState(null);
    const params = new URLSearchParams(location.search);
    const selectedCause = CAUSES.find((cause) => cause.key === params.get("cause"))?.key ?? null;
    const causeLabelOf = (key) => CAUSES.find((cause) => cause.key === key).label;
    const selectedPath = isFolder(params.get("path")) ? params.get("path") : null;
    const pushParams = usePushParams();
    const history = useHistory();
    const search = params.get("q") || "";
    // Replaced, not pushed: one history entry per keystroke would make Back unusable.
    const setSearch = (value) => {
        const next = new URLSearchParams(location.search);
        if (value) next.set("q", value); else next.delete("q");
        history.replace({ pathname: location.pathname, search: `?${next.toString()}` });
    };

    const { pages, traffic, chart, rankedPaths, generatedFrom } = fixture;
    const lastDay = lastDayOf({ generatedFrom, chart });
    const firstDay = useMemo(() => firstDayOf({ chart, pages }), [chart, pages]);
    const range = rangeOf(params, lastDay, firstDay);
    const { from, to } = range;
    const rangeFilterLabel = filterLabelOf(range);
    const measures = useMemo(() => measuresOf(fixture), [fixture]);
    const measure = measureOf(measureKeyOf(measures));
    const lossMeasures = useMemo(() => LOSS_ORDER.filter((key) => measures.includes(key)).map(measureOf), [measures]);
    // With no measure there is no loss and no criticality, so no row is filtered by it.
    const lossShown = measures.length > 0;
    const sort = lossShown ? "impact" : "importance";

    const issueRows = useMemo(() => pages.flatMap((page) => rowsOf(page, page.issues)), [pages]);
    // A row's level on each measure the workspace has, impressions first as the pill's tooltip names them; the row's criticality is the highest.
    const rowsWithCriticality = useMemo(() => {
        const readers = lossMeasures.map(({ site, readingOf }) => {
            const siteMonthly = siteMonthlyOf(traffic, site.key);
            return (issue) => ({ ...criticalityOf(readingOf(issue)?.lost * 30, siteMonthly), name: site.name });
        });
        return issueRows.map((row) => {
            const criticalities = readers.map((read) => read(row.issue));
            return { ...row, criticalities, criticality: highestOf(criticalities) };
        });
    }, [issueRows, lossMeasures, traffic]);
    // Every figure on the page counts the rows whose issues started inside the range.
    const allRows = useMemo(() => rowsWithCriticality.filter(({ issue }) => isInside({ from, to })(issue.since)), [rowsWithCriticality, from, to]);
    const searched = useMemo(() => {
        const term = search.trim().toLowerCase();
        return allRows.filter(({ page }) => page.uri.toLowerCase().includes(term));
    }, [allRows, search]);
    // The rows with an issue of one cause and under one folder, either of them null for any, each row keeping all its issues, with the search applied.
    const rowsWith = useCallback((cause, path) => searched
        .filter(({ page }) => !path || isUnder(path)(page.uri))
        .filter(({ issues }) => !cause || issues.some((issue) => causesOf(issue).some((each) => each.key === cause))), [searched]);
    const lossOfRows = useCallback((rows) => lossOf(rows, lossMeasures), [lossMeasures]);

    const filterable = useMemo(() => rowsWith(selectedCause, selectedPath), [rowsWith, selectedCause, selectedPath]);
    const selectedCriticality = lossShown ? criticalityFilterOf(params.get("criticality")) : ALL;
    const listed = useMemo(() => [...rowsUnder(selectedCriticality, filterable)].sort(SORTS[sort].compare), [selectedCriticality, filterable, sort]);
    const shown = useMemo(() => listed.slice(0, MAX_ROWS), [listed]);
    const counts = useMemo(() => countsOf(filterable), [filterable]);
    const filtered = Boolean(selectedCause || selectedPath || selectedCriticality !== ALL);
    const causes = useMemo(() => CAUSES
        .map((cause) => ({ cause, rows: rowsWith(cause.key, selectedPath) }))
        .filter(({ rows }) => rows.length > 0)
        .map(({ cause, rows }) => ({ cause, rows: rows.length, ...lossOfRows(rows) }))
        .sort((a, b) => b.impact - a.impact || b.rows - a.rows), [rowsWith, selectedPath, lossOfRows]);
    const causesTotal = useMemo(() => lossOfRows(rowsWith(null, selectedPath)), [lossOfRows, rowsWith, selectedPath]);
    const { folders, foldersTotal } = useMemo(() => {
        const rows = rowsWith(selectedCause, null);
        const tree = folderTree(rows, lossOfRows);
        return { folders: lossShown ? withLoss(tree) : tree, foldersTotal: lossOfRows(rows) };
    }, [rowsWith, selectedCause, lossOfRows, lossShown]);
    const siteTraffic = useMemo(() => traffic.filter((day) => isInside({ from, to })(day.date)), [traffic, from, to]);
    const charted = chartedKeys ?? [measure.site.key];
    // A tile turns its figure on or off; the last one on stays on.
    const toggleCharted = (key) => setChartedKeys(charted.includes(key) ? (charted.length > 1 ? charted.filter((each) => each !== key) : charted) : [...charted, key]);
    const chartMetrics = KPIS.filter(({ key }) => charted.includes(key)).filter((metric) => siteTraffic.some((day) => metric.daily(day) != null));
    const pagesWithAnIssue = useMemo(() => new Set(allRows.map(({ page }) => page.id)).size, [allRows]);
    const chartDays = useMemo(() => (chart ? daysOf(chart) : []), [chart]);
    const selectCause = (key) => pushParams({ cause: key === selectedCause ? null : key });
    const selectPath = (path) => pushParams({ path: path === selectedPath ? null : path });

    return (
        <Wrap>
            <Header>
                <Subtitle>{pagesWithAnIssue.toLocaleString()} pages with an issue</Subtitle>
                <RangeSelector
                    presets={presetsEndingOn(lastDay).map(({ key, label, from, to }) => ({ key, label, startIso: from, endIso: to }))}
                    preset={range.key}
                    startDate={range.from}
                    endDate={range.to}
                    minDate={firstDay}
                    maxDate={lastDay}
                    onChange={({ preset, startIso, endIso }) => pushParams(preset === "custom"
                        ? { zoom: null, from: startIso.slice(0, 10), to: endIso.slice(0, 10) }
                        : { ...DEFAULT_RANGE_PARAMS, zoom: preset !== DEFAULT_RANGE ? preset : null })}
                />
            </Header>

            <KpiBar traffic={traffic} range={range} charted={charted} onToggle={toggleCharted} />

            {chartMetrics.length > 0 && <TrafficOverview traffic={siteTraffic} metrics={chartMetrics}
                onRange={(start, end) => pushParams({ zoom: null, from: start, to: end })} />}

            <Controls
                filters={[
                    ...(rangeFilterLabel ? [{ label: rangeFilterLabel, onDelete: () => pushParams(DEFAULT_RANGE_PARAMS) }] : []),
                    ...(selectedCause ? [{ label: `Cause: ${causeLabelOf(selectedCause)}`, onDelete: () => selectCause(selectedCause) }] : []),
                    ...(selectedPath ? [{ label: `Path: ${selectedPath}`, onDelete: () => selectPath(selectedPath) }] : []),
                ]}
                onClearFilters={() => pushParams({ ...DEFAULT_RANGE_PARAMS, cause: null, path: null })}
            />

            <Breakdown subtitle={lossShown ? "Lost a month" : "Issues by cause and by path"}>
                <CauseBreakdown causes={causes} total={causesTotal} measures={lossMeasures} selectedCause={selectedCause} onSelect={selectCause} />
                <PathBreakdown folders={folders} total={foldersTotal} measures={lossMeasures} selectedPath={selectedPath} onSelect={selectPath} />
            </Breakdown>

            <TableBar>
                <TableTitle $filtered={filtered}>
                    {filtered
                        ? <>
                            {[selectedPath, selectedCause && causeLabelOf(selectedCause), selectedCriticality !== ALL && CRITICALITIES[selectedCriticality].label].filter(Boolean).join(" · ")}
                            {" — "}{shownOf(listed)}{listed.length > MAX_ROWS && " —"} of {allRows.length.toLocaleString()} issues
                        </>
                        : <>Issues by {SORTS[sort].label} — {shownOf(listed)}</>}
                </TableTitle>
                <TextField
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search URL…"
                    variant="outlined"
                    size="small"
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment>,
                        endAdornment: search && (
                            <InputAdornment position="end">
                                <IconButton aria-label="Clear search" size="small" onClick={() => setSearch("")}><MdClear /></IconButton>
                            </InputAdornment>
                        ),
                        style: { fontSize: "0.8rem", background: "#fff" },
                    }}
                />
            </TableBar>

            {lossShown && (
                <CriticalityFilters
                    counts={counts}
                    selected={selectedCriticality}
                    onSelect={(key) => pushParams({ criticality: key === ALL ? null : key })}
                />
            )}

            <IssueRows
                rows={shown}
                emptyText={selectedCriticality !== ALL ? `No ${CRITICALITIES[selectedCriticality].label.toLowerCase()} issues` : null}
                chartDays={chartDays}
                rankedPaths={rankedPaths}
                lossMeasures={lossMeasures}
                measure={measure}
            />
        </Wrap>
    );
}

const Wrap = styled.div`
    padding-bottom: 40px;
`;

/** The count on the left, the date range on the right; on a narrow screen the range wraps under it. */
const Header = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px 16px;
    margin-bottom: 18px;
    @media (max-width: 600px) { padding: 0 16px; }
`;

/** The Trends date picker as a segmented control: taller, the selected option filled dark. */
const RangeSelector = styled(DateRangeSelector)`
    ${PresetRow} { min-height: 36px; }
    ${PresetBtn} {
        align-self: stretch;
        padding: 0 12px;
        font-size: 0.78rem;
        &[aria-pressed="true"], &[aria-pressed="true"]:hover { background: #0f172a; color: #fff; }
        &:hover { background: #f1f5f9; color: #0f172a; }
    }
`;

const Subtitle = styled.div`
    font-size: 0.82rem;
    color: #64748b;
`;

const TableBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 0 8px 2px;
    @media (max-width: 600px) { padding: 0 16px; }
`;

/** A small grey label; while a filter is set, sentence case in the accent colour at the rows' size, so paths read as written. */
const TableTitle = styled.div`
    font-weight: 700;
    ${({ $filtered }) => ($filtered
        ? `font-size: 0.86rem; color: ${ACCENT};`
        : "font-size: 0.62rem; letter-spacing: 1.2px; text-transform: uppercase; color: #64748b;")}
`;
